export interface EmbedTheme {
  primary: string;
  primaryHover: string;
  primaryDisabled?: string;
  primarySoft?: string;
  primarySoftBg?: string;
  primaryDark?: string;
  accentBadgeBg?: string;
  accentBadgeText?: string;
  accentSpark?: string;
  botBubbleBg?: string;
  botBubbleBorder?: string;
  textMuted?: string;
  // Dark-mode-aware surface slots (defaults preserve the existing light look)
  surfaceBg?: string;
  surfaceBorder?: string;
  divider?: string;
  textPrimary?: string;
  textSecondary?: string;
  userAvatarBg?: string;
  userAvatarBorder?: string;
  cardBg?: string;
  cardBorder?: string;
  botBubbleText?: string;
}

const DEFAULT_DISABLED = '#cccccc';

const VIOLET_THEME: EmbedTheme = {
  primary: '#7c5cd9',
  primaryHover: '#6a48cc',
  primaryDisabled: DEFAULT_DISABLED,
  primarySoft: '#d4c9ee',
  primarySoftBg: '#f4f1fa',
  primaryDark: '#3d2b8f',
  accentBadgeBg: '#e8e1f7',
  accentBadgeText: '#5b3fb8',
  accentSpark: '#f5c945',
  botBubbleBg: '#ffffff',
  botBubbleBorder: '#d4c9ee',
  botBubbleText: '#333333',
  textMuted: '#6b7280',
  surfaceBg: '#ffffff',
  surfaceBorder: '#e0e0e0',
  divider: '#f0f0f0',
  textPrimary: '#333333',
  textSecondary: '#646877',
  userAvatarBg: '#f0f0f0',
  userAvatarBorder: '#bbbbbb',
  cardBg: '#fafbfc',
  cardBorder: '#e0e0e0',
};

const VIOLET_DARK_THEME: EmbedTheme = {
  primary: '#7c5cd9',
  primaryHover: '#8e6fe3',
  primaryDisabled: '#3a3744',
  primarySoft: '#3a2a5e',
  primarySoftBg: '#0e0a1a',
  primaryDark: '#b8a8e0',
  accentBadgeBg: '#2d1f4d',
  accentBadgeText: '#b8a8e0',
  accentSpark: '#f5c945',
  botBubbleBg: '#241638',
  botBubbleBorder: '#3a2a5e',
  botBubbleText: '#e8e3f5',
  textMuted: '#9c9aab',
  surfaceBg: '#1a1228',
  surfaceBorder: '#3a2a5e',
  divider: '#2d1f4d',
  textPrimary: '#e8e3f5',
  textSecondary: '#b8b6c4',
  userAvatarBg: '#3a3744',
  userAvatarBorder: '#5a5764',
  cardBg: '#1a1228',
  cardBorder: '#3a2a5e',
};

/**
 * Theme registry for the embedded chatbot.
 *
 * To add a theme for a new parent app:
 *   1. Add a new entry below with a unique ID (e.g. 'app-orange').
 *   2. The parent app loads the iframe with `?theme=<id>`.
 *
 * Dark mode: parent apps pass `?theme=<id>-dark` when the user is in dark mode.
 * The iframe does NOT auto-switch — parent must reload the iframe on mode change.
 *
 * Theme IDs are normalized to lowercase, so use lowercase keys here.
 */
export const EMBED_THEMES: Record<string, EmbedTheme> = {
  default: {
    primary: '#4890e4',
    primaryHover: '#357abd',
    primaryDisabled: DEFAULT_DISABLED,
  },
  // For now all three parent apps share the same violet palette.
  // Each entry is kept separate so future redesigns can diverge per app
  // without re-wiring the embed (the parent app still passes its own `?theme=`).
  'app-iplug': VIOLET_THEME,
  'app-orange': VIOLET_THEME,
  'app-intouch': VIOLET_THEME,
  'app-iplug-dark': VIOLET_DARK_THEME,
  'app-orange-dark': VIOLET_DARK_THEME,
  'app-intouch-dark': VIOLET_DARK_THEME,
};

export const resolveTheme = (rawId: string | null | undefined): EmbedTheme => {
  if (!rawId) return EMBED_THEMES.default;
  const id = rawId.trim().toLowerCase();
  return EMBED_THEMES[id] || EMBED_THEMES.default;
};

export const themeToCssVars = (theme: EmbedTheme): React.CSSProperties => {
  return {
    ['--rc-primary' as any]: theme.primary,
    ['--rc-primary-hover' as any]: theme.primaryHover,
    ['--rc-primary-disabled' as any]: theme.primaryDisabled || DEFAULT_DISABLED,
    ['--rc-primary-soft' as any]: theme.primarySoft || theme.primary,
    ['--rc-primary-soft-bg' as any]: theme.primarySoftBg || '#ffffff',
    ['--rc-primary-dark' as any]: theme.primaryDark || theme.primary,
    ['--rc-accent-badge-bg' as any]: theme.accentBadgeBg || '#f0f0f0',
    ['--rc-accent-badge-text' as any]: theme.accentBadgeText || theme.primary,
    ['--rc-accent-spark' as any]: theme.accentSpark || '#f5c945',
    ['--rc-bot-bubble-bg' as any]: theme.botBubbleBg || '#ffffff',
    ['--rc-bot-bubble-border' as any]: theme.botBubbleBorder || '#e0e0e0',
    ['--rc-bot-bubble-text' as any]: theme.botBubbleText || '#333333',
    ['--rc-text-muted' as any]: theme.textMuted || '#6b7280',
    ['--rc-surface-bg' as any]: theme.surfaceBg || '#ffffff',
    ['--rc-surface-border' as any]: theme.surfaceBorder || '#e0e0e0',
    ['--rc-divider' as any]: theme.divider || '#f0f0f0',
    ['--rc-text-primary' as any]: theme.textPrimary || '#333333',
    ['--rc-text-secondary' as any]: theme.textSecondary || '#646877',
    ['--rc-user-avatar-bg' as any]: theme.userAvatarBg || '#f0f0f0',
    ['--rc-user-avatar-border' as any]: theme.userAvatarBorder || '#bbbbbb',
    ['--rc-card-bg' as any]: theme.cardBg || '#fafbfc',
    ['--rc-card-border' as any]: theme.cardBorder || '#e0e0e0',
  };
};
